import { forwardRef, PropsWithChildren } from 'react'

import { Button, ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ViverseIconButtonSize = 'S' | 'M' | 'L' | 'custom'
type ViverseIconButtonVariant = 'empty' | 'filled'

export type ViverseIconButtonProps = PropsWithChildren<Omit<ButtonProps, 'variant' | 'size'>> & {
  variant?: ViverseIconButtonVariant
  size?: ViverseIconButtonSize
  label?: string
  labelClassName?: string
  tooltip?: string
}

const BASE = 'group relative flex items-center justify-center rounded-full backdrop-blur-md'

const VARIANT: Record<ViverseIconButtonVariant, string> = {
  empty:
    'bg-transparent hover:bg-vive-primary-60 active:bg-vive-primary-70 disabled:hover:bg-transparent transition-colors duration-[130ms] ease-out',
  filled:
    'bg-vive-surface-normal hover:bg-vive-primary-70 active:bg-vive-primary-80 disabled:bg-vive-primary-60 disabled:hover:bg-vive-primary-60 transition-colors duration-[130ms] ease-out',
}

const SIZE_ICON: Record<ViverseIconButtonSize, string> = {
  S: 'size-8.5 px-[7px] py-[7px]',
  M: 'size-12 px-3 py-3',
  L: 'size-14 px-3.5 py-3.5',
  custom: '',
}

const SIZE_ICON_LABEL: Record<ViverseIconButtonSize, string> = {
  S: 'h-8.5 w-fit px-4 py-1 gap-2',
  M: 'h-12 w-fit px-5 py-3 gap-2',
  L: 'h-14 w-fit px-6 py-3 gap-2',
  custom: '',
}

const LABEL: Record<ViverseIconButtonSize, string> = {
  S: 'buttonS-omni-styles',
  M: 'buttonM-omni-styles',
  L: 'buttonL-omni-styles',
  custom: '',
}

const ViverseIconButton = forwardRef<HTMLButtonElement, ViverseIconButtonProps>(
  (
    {
      children,
      className,
      variant = 'empty',
      size = 'L',
      label = '',
      labelClassName,
      tooltip = '',
      ...rest
    },
    ref,
  ) => {
    const withLabel = !!label
    return (
      <Button
        ref={ref}
        className={cn(
          BASE,
          VARIANT[variant],
          withLabel ? SIZE_ICON_LABEL[size] : SIZE_ICON[size],
          className,
        )}
        type="button"
        {...rest}
      >
        {children}
        {withLabel && (
          <span className={cn('text-vive-primary-20', LABEL[size], labelClassName)}>{label}</span>
        )}
        {tooltip && (
          <span
            className={cn(
              'pointer-events-none absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2',
              'translate-y-1 opacity-0 transition-all delay-[210ms] duration-[130ms] ease-out',
              'group-hover:translate-y-0 group-hover:opacity-100',
              'text-vive-white bg-vive-primary-70 max-h-[162px] min-h-7 w-fit rounded-[3px] px-4 py-1.5',
            )}
            style={{ boxShadow: '3px 3px 6px 0 rgba(19, 27, 38, 0.20)' }}
          >
            {tooltip}
          </span>
        )}
      </Button>
    )
  },
)

ViverseIconButton.displayName = 'ViverseIconButton'

export default ViverseIconButton
